import type { MediaItem } from "./media";
import { childrenEducation, cultureDialogue, reliefResilience, womenGirls } from "./media";

/**
 * The four public programme pillars. Every documented activity from the
 * organisation's existing programme pages sits beneath one of these, nothing
 * has been dropped, and no new activity has been invented.
 */
export type Pillar = {
  slug: string;
  title: string;
  shortTitle: string;
  /** One-sentence outcome, used on cards. */
  outcome: string;
  /** Two example activities for the homepage card. */
  examples: [string, string];
  cta: string;
  intro: string;
  approach: string[];
  activities: { title: string; description: string }[];
  evidence: { label: string; value: string }[];
  image: MediaItem;
  /**
   * Optional alternative for the homepage card, where type sits on the
   * photograph. Set it when the lead image is too busy behind text: contrast can
   * be fixed with a scrim, visual clutter cannot.
   */
  cardImage?: MediaItem;
  gallery: MediaItem[];
  relatedProjects: string[];
  source: string;
};

export const pillars: Pillar[] = [
  {
    slug: "women-girls",
    title: "Women & Girls",
    shortTitle: "Women & Girls",
    outcome:
      "Women and girls gain menstrual health knowledge, wellbeing support and skills that convert into household income.",
    examples: ["Empowering with Dignity sessions", "Reusable pad and soap making"],
    cta: "Explore Women & Girls",
    intro:
      "HISCUS works with women and girls on the things that decide whether a month goes well or badly: whether menstruation is manageable, whether there is somewhere to take stress, and whether a skill can be turned into income. Sessions are run where participants already are: in wards, workplaces and community halls.",
    approach: [
      "Sessions are requested by, and planned with, the local ward, school or community group.",
      "Menstrual health education is paired with practical making, so participants leave with a product and a method, not only a talk.",
      "Wellbeing and livelihood work run alongside each other rather than as separate projects.",
    ],
    activities: [
      {
        title: "Menstrual health education",
        description:
          "Group sessions covering menstrual health, hygiene and dignity, delivered through the Empowering with Dignity programme.",
      },
      {
        title: "Reusable pad making",
        description:
          "Participants cut, stitch and take home reusable sanitary pads, and learn how to make more.",
      },
      {
        title: "Psychosocial wellbeing and stress management",
        description:
          "Facilitated sessions giving participants practical tools for stress, alongside space to talk.",
      },
      {
        title: "Livelihood and vocational skills",
        description:
          "Soap making, bangle making and compost production, chosen because they can be run from home with low start-up cost.",
      },
    ],
    evidence: [
      { label: "Ropeway Sadak, 13 June 2025", value: "25 participants" },
      { label: "Bethanchowk, 28 January 2025", value: "18 women" },
      { label: "Bhaktapur, 17 March 2025", value: "20 participants" },
    ],
    image: womenGirls[0],
    gallery: womenGirls.slice(1),
    relatedProjects: ["empowering-with-dignity"],
    source: "https://himshikharanepal.org.np/homepage/what-we-do/women-empowerment/",
  },
  {
    slug: "children-education",
    title: "Children & Education",
    shortTitle: "Children & Education",
    outcome:
      "Children have a safe place to live and study, and stay in education long enough for it to change what comes next.",
    examples: ["Sarangkot Nestling Home", "Sponsorship and teacher support"],
    cta: "Explore Children & Education",
    intro:
      "The Sarangkot Nestling Home is the anchor of this pillar: a residential home completed in 2018 and run with The Nestling Trust UK. Around it sit the quieter forms of support that keep children in school: sponsorship, teacher support and supervised learning before and after the school day.",
    approach: [
      "Residential care is delivered as a long-term commitment, not a short project cycle.",
      "Education support follows the child through school rather than stopping at enrolment.",
      "Children's identities and personal histories are protected in all public material.",
    ],
    activities: [
      {
        title: "Sarangkot Nestling Home",
        description:
          "A residential home completed on 13 April 2018 and operated in partnership with The Nestling Trust UK.",
      },
      {
        title: "Education sponsorship",
        description: "Support that keeps children enrolled and equipped for school.",
      },
      {
        title: "Teacher support",
        description: "Working with schools to strengthen the teaching children receive.",
      },
      {
        title: "Before and after-school learning",
        description:
          "Supervised study and activity sessions in Pokhara and Chitwan, outside school hours.",
      },
    ],
    evidence: [
      { label: "Home completed", value: "13 April 2018" },
      { label: "Girls supported, as reported for 2025", value: "11" },
      { label: "Progressed to higher education or careers", value: "2 young people" },
    ],
    image: childrenEducation[0],
    gallery: childrenEducation.slice(1),
    relatedProjects: ["sarangkot-nestling-home"],
    source: "https://himshikharanepal.org.np/homepage/what-we-do/education-support/",
  },
  {
    slug: "health-relief-resilience",
    title: "Health, Relief & Community Resilience",
    shortTitle: "Health, Relief & Resilience",
    outcome:
      "Communities recover faster after shocks, and keep the water, health access and infrastructure that recovery depends on.",
    examples: ["Bethanchowk water tank", "Health camps and health post support"],
    cta: "Explore Health, Relief & Resilience",
    intro:
      "This pillar covers both the immediate response (flood relief in Thapathali, supplies to a health post in Bethanchowk) and the slower work that makes the next shock less damaging: water storage, access paths and protective walls that stay in the community long after a project closes.",
    approach: [
      "Relief is delivered through existing local structures: wards, health posts and community groups.",
      "Every response is asked to leave something durable behind: a tank, a path, a wall.",
      "Health education and health camps run alongside infrastructure rather than separately.",
    ],
    activities: [
      {
        title: "Community and school health education",
        description: "Health education sessions and health camps run with schools and communities.",
      },
      {
        title: "Flood response",
        description:
          "Thapathali flood response on 8–9 October 2024: clothing support for affected children and a community water-storage tank.",
      },
      {
        title: "Health post support",
        description: "Supplies and support to the Bethanchowk health post on 1 January 2025.",
      },
      {
        title: "Water, paths and community infrastructure",
        description:
          "The Bethanchowk water tank, a 700-metre shortcut path for Sarangkot villagers, and protective-wall work for the Bhaktapur Old Age Home.",
      },
    ],
    evidence: [
      { label: "Bethanchowk water tank", value: "Oct 2024 – 9 July 2025" },
      { label: "Households with restored water access", value: "60+" },
      { label: "Sarangkot access path", value: "700 metres" },
    ],
    image: reliefResilience[0],
    /*
     * The card image changed.
     *
     * It used to be the aerial view of a hillside community site, which is a
     * genuine HISCUS photograph and the wrong one for this pillar: with a
     * heading over it, it reads as a school or a farm, not as health access,
     * flood response or relief. This pillar's first named activities are health
     * education, flood response and health post support, and the card should
     * look like one of them.
     *
     * The riverside assessment is that photograph. It is response work at
     * Thapathali, it is calm and open exactly where the type sits, and there is
     * no child in the frame. The riverside distribution photograph is the
     * stronger image and is deliberately not used: it contains identifiable
     * children and its consent check has not come back.
     *
     * The lead image for the pillar page itself is unchanged. The health post
     * supplies handover is exactly what "health post support" means, and it is
     * shown at a size where its detail reads rather than turning into noise.
     */
    cardImage: reliefResilience[4],
    gallery: reliefResilience.slice(1),
    relatedProjects: ["bethanchowk-water-tank", "thapathali-flood-response"],
    source: "https://himshikharanepal.org.np/homepage/what-we-do/health-camp-program/",
  },
  {
    slug: "culture-environment-dialogue",
    title: "Culture, Environment & Dialogue",
    shortTitle: "Culture, Environment & Dialogue",
    outcome:
      "Local knowledge, waste and difficult public questions are handled openly, with communities in the room.",
    examples: ["Waste management and composting", "Roundtables, podcasts and documentaries"],
    cta: "Explore Culture, Environment & Dialogue",
    intro:
      "Some problems do not respond to delivery alone. This pillar holds the work of preserving cultural practice, dealing with waste at ward level, and putting uncomfortable subjects into recorded public conversation: gendered violence, mental health, menstrual dignity.",
    approach: [
      "Dialogue is recorded and published so it outlives the room it happened in.",
      "Environmental work starts at ward level, where waste decisions are actually made.",
      "Academic and community voices are put on the same platform.",
    ],
    activities: [
      {
        title: "Cultural preservation",
        description: "Work to record and sustain local cultural practice.",
      },
      {
        title: "Waste management and composting",
        description:
          "Waste-management work and compost workshops, including a Ward 3 waste-management discussion in 2025.",
      },
      {
        title: "Academic roundtables",
        description: "Including a roundtable on gendered violence held in 2024.",
      },
      {
        title: "Podcasts and documentaries",
        description:
          "Recorded conversations on waste, mental health, feminism, menstrual dignity and women's resilience.",
      },
    ],
    evidence: [
      { label: "Gendered violence roundtable", value: "2024" },
      { label: "Ward 3 waste-management discussion", value: "2025" },
    ],
    image: cultureDialogue[0],
    gallery: cultureDialogue.slice(1),
    relatedProjects: ["insights-dialogue-series"],
    source: "https://himshikharanepal.org.np/homepage/what-we-do/insights-dialogue-series/",
  },
];

export const getPillar = (slug: string) => pillars.find((p) => p.slug === slug);
